import { FETCH_POLICY_NO_CACHE } from "@/services/web/api.const";
import {
  CHANGE_DECREE_CATEGORY,
  QUERY_DECREES_PAGINATED,
  QUERY_TEMPORARY_DECREE_BY_ID,
  QUERY_TEMPORARY_DECREES_PAGINATED,
  REMOVE_DECREE,
} from "services/web/api-decree.const";
import {
  CreateDecreeInput,
  PaginatedDecreeArgs,
  PaginatedDecrees,
  PermitDecreeRaw,
  TemporaryDecreeRaw,
} from "types/decree.type";
import { DecreeCategory } from "@/types/gql/graphql";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { TemporaryDecreeQueryVariables } from "@/types/gql/graphql";
import { SkipToken, skipToken } from "@apollo/client/react";
import { useAuthenticatedApiAsync } from "@/services/web/use-api";
import { useMemo } from "react";

const mapPaginatedDecrees = (raw: {
  results: readonly unknown[];
  totalPages: number;
  totalCount: number;
}): PaginatedDecrees => ({
  totalPages: raw.totalPages,
  totalCount: raw.totalCount,
  results: raw.results.map((item) => {
    const { temporaryType, permitType, ...rest } = item as TemporaryDecreeRaw & PermitDecreeRaw;
    return { ...rest, type: temporaryType ?? permitType ?? null };
  }),
});

export const useQueryDecreesPaginated = (input: PaginatedDecreeArgs, options?: { skip?: boolean }) => {
  const {
    data = { decreesPaginated: { results: [], totalPages: 0, totalCount: 0 } },
    loading,
    refetch: refetchDecree,
  } = useTrackedQuery(QUERY_DECREES_PAGINATED, {
    variables: {
      ...input,
      sortOrder: input.sortOrder,
    },
    skip: !input.cityId || options?.skip,
    actionName: "QueryDecreesPaginated",
  });

  const mappedDecreeData = useMemo(() => mapPaginatedDecrees(data.decreesPaginated), [data]);

  return {
    decreeData: mappedDecreeData,
    isLoading: loading,
    refetchDecree,
  };
};

export const useQueryTemporaryDecreesPaginated = (input: PaginatedDecreeArgs, options?: { skip?: boolean }) => {
  const {
    data = { temporaryDecreesPaginated: { results: [], totalPages: 0, totalCount: 0 } },
    loading,
    refetch: refetchDecree,
  } = useTrackedQuery(QUERY_TEMPORARY_DECREES_PAGINATED, {
    variables: {
      ...input,
      sortOrder: input.sortOrder,
    },
    skip: !input.cityId || options?.skip,
    actionName: "QueryTemporaryDecreesPaginated",
  });

  const mappedDecreeData = useMemo(() => mapPaginatedDecrees(data.temporaryDecreesPaginated), [data]);

  return {
    decreeData: mappedDecreeData,
    isLoading: loading,
    refetchDecree,
  };
};

export const useRemoveDecree = (refetchDecree: () => void) => {
  const [removeDecree] = useTrackedMutation(REMOVE_DECREE, {
    actionName: "RemoveDecree",
    onCompleted: () => {
      refetchDecree();
    },
    onError: (error) => {
      return error;
    },
  });

  return { removeDecree };
};

export const useUpdateDecreeCategory = (refetchDecree?: () => void) => {
  const [updateDecreeCategory] = useTrackedMutation(CHANGE_DECREE_CATEGORY, {
    actionName: "UpdateDecreeCategory",
    onCompleted: () => {
      if (refetchDecree) {
        refetchDecree();
      }
    },
  });

  return {
    updateDecreeCategory,
  };
};

export const useCreateDecreeFile = () => {
  const { mutateAsync, loading, error } = useAuthenticatedApiAsync();

  const options = {
    method: "POST",
  };

  const createDecreeFile = async (input: CreateDecreeInput) => {
    const newForm = new FormData();
    const variables = { createDecreeInput: { ...input, attachedFile: null } };

    const permanentDecreeOperations = {
      query: `mutation CreatePermanentDecree($createDecreeInput: CreatePermanentDecreeInput!) {
          createPermanentDecree(createDecreeInput: $createDecreeInput) {
            id
          }
        }`,
      variables,
    };

    const temporaryDecreeOperations = {
      query: `mutation CreateTemporaryDecree($createDecreeInput: CreateTemporaryDecreeInput!) {
          createTemporaryDecree(createDecreeInput: $createDecreeInput) {
            id
          }
        }`,
      variables,
    };

    const permitDecreeOperations = {
      query: `mutation CreatePermitDecree($createDecreeInput: CreatePermitDecreeInput!) {
          createPermitDecree(createDecreeInput: $createDecreeInput) {
            id
          }
        }`,
      variables,
    };

    let decreeOperations;
    switch (input.category) {
      case DecreeCategory.Permit:
        decreeOperations = permitDecreeOperations;
        break;
      case DecreeCategory.Temporary:
        decreeOperations = temporaryDecreeOperations;
        break;
      default:
        decreeOperations = permanentDecreeOperations;
    }

    newForm.append("operations", JSON.stringify(decreeOperations));

    const map = { "0": ["variables.createDecreeInput.attachedFile"] };
    newForm.append("map", JSON.stringify(map));
    newForm.append("0", input.attachedFile);
    await mutateAsync({ ...options, body: newForm });
  };

  return {
    createDecreeFile,
    loading,
    error,
  };
};

export const useQueryTemporaryDecree = (variables: TemporaryDecreeQueryVariables | SkipToken) => {
  const options = useMemo(() => {
    if (variables === skipToken) {
      return skipToken;
    }
    return {
      variables,
      fetchPolicy: FETCH_POLICY_NO_CACHE,
      actionName: "QueryTemporaryDecree",
    };
  }, [variables]);

  const { data, loading } = useTrackedQuery(QUERY_TEMPORARY_DECREE_BY_ID, options);
  return {
    temporaryDecreeData: data?.temporaryDecree,
    isTemporaryDecreeDataLoading: loading,
  };
};
